import { useTranslation } from "react-i18next";

import { Copy } from "@/assets/icons/icons";
import { useAlert } from "@/hooks/useAlert";
import { AlertTypes } from "@/utils/atoms";

/**
 * The "enter this key manually" fallback shown alongside the 2FA QR code,
 * shared by the initial app setup (`AuthenticatorAppForm`) and the
 * management "Change 2FA method" flow (`TwoFactorManualQrCodeField`) so
 * both render the identical, correctly-branded copy control.
 */
export const ManualKeyField = ({ manualCode }: { manualCode?: string }) => {
  const { t } = useTranslation();
  const showAlert = useAlert();

  const copyManualKey = () => {
    if (!manualCode) {
      return;
    }

    navigator.clipboard
      .writeText(manualCode)
      .then(() => {
        showAlert({
          type: AlertTypes.success,
          text: t("portal:authenticator.app.setup.step1.copied"),
        });
      })
      .catch(() => {
        showAlert({
          type: AlertTypes.error,
          text: t("portal:authenticator.app.setup.step1.copy-error"),
        });
      });
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <span className="text-base font-semibold text-foreground-primary">
        {t("portal:authenticator.app.setup.step1.your-manual-key")}
      </span>
      <div className="flex items-center justify-between gap-3">
        <span className="break-all text-base text-foreground-primary">
          {manualCode}
        </span>
        <button
          type="button"
          className="flex shrink-0 items-center"
          onClick={copyManualKey}
          aria-label={t("translation:clipboardField.copy")}
        >
          <Copy className="h-5 w-5 text-foreground-primary" />
        </button>
      </div>
    </div>
  );
};
